import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api/client';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState([]);          // ordered list of favorited product ids (newest first)
  const [products, setProducts] = useState([]); // hydrated product objects for /favorites page
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds([]);
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get('/favorites');
      setIds(r.data.ids || []);
      setProducts(r.data.products || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFavorite = (productId) => ids.includes(productId);

  const toggle = async (product) => {
    if (!user) {
      toast.info('Please sign in to save favourites');
      return { requiresLogin: true };
    }
    const pid = product.id;
    const currentlyFav = ids.includes(pid);
    // optimistic
    if (currentlyFav) {
      setIds(prev => prev.filter(x => x !== pid));
      setProducts(prev => prev.filter(p => p.id !== pid));
    } else {
      setIds(prev => [pid, ...prev]);
      setProducts(prev => [product, ...prev.filter(p => p.id !== pid)]);
    }
    try {
      if (currentlyFav) {
        await api.delete(`/favorites/${pid}`);
        toast.success('Removed from your list');
      } else {
        await api.post(`/favorites/${pid}`);
        toast.success('Saved to your list');
      }
    } catch (e) {
      // revert
      toast.error('Could not update your list');
      refresh();
    }
    return { requiresLogin: false };
  };

  return (
    <FavoritesContext.Provider value={{ ids, products, loading, isFavorite, toggle, refresh, count: ids.length }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
